import { Redirect } from "react-router-dom";
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useDispatch, useSelector } from "react-redux";
import { actionCreators, State } from "../state/index";
import { bindActionCreators } from "redux";
import AuthService from "../services/authService";
import "./login.css";
import logo from "../assets/images/logo.png";
import useAlertStore from "../store/useAlertStore";

// Add this to prevent duplicate alerts
const ALERT_DEBOUNCE_TIME = 2000; // 2 seconds
let lastAlertTime = 0;

function Login(props: any) {
  const [user, setUser] = useState({
    username: "",
    password: "",
    loading: false,
    loggedIn: false,
  });
  const [redirect, setRedirect] = useState(false);
  
  // Use the alert store
  const showSuccessAlert = useAlertStore(state => state.success);

  const dispatch = useDispatch();
  const { login, loginFailed, messageAction } = bindActionCreators(actionCreators.authActions, dispatch);
  const state = useSelector((state: State) => state);
  const { isLoggedIn } = state.authReducer;
  const { message } = state.messageReducer;
  const languageData = state.languageReducer;

  useEffect(() => {
    // Handle redirect after successful login with a delay
    if (user.loggedIn) {
      const timer = setTimeout(() => {
        setRedirect(true);
      }, 1500); // 1.5 second delay
      return () => clearTimeout(timer);
    }
  }, [user.loggedIn]);

  // Function to show alert with debounce to prevent duplicates
  const showAlert = (message: string) => {
    const now = Date.now();
    if (now - lastAlertTime > ALERT_DEBOUNCE_TIME) {
      showSuccessAlert(message);
      lastAlertTime = now;
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setUser(prev => ({ ...prev, loading: true }));

    try {
      const response = await AuthService.login(user.username, user.password);
      dispatch(login(response.data));
      setUser(prev => ({ ...prev, loading: false, loggedIn: true }));
      // Clear any previous error messages
      dispatch(messageAction(""));
      // Use debounced alert function to prevent duplicates
      showAlert(languageData?.data?.login?.success);
    } catch (error: any) {
      const message = (error.response?.data?.message) || error.message || languageData?.data?.login?.failed;
      dispatch(loginFailed());
      dispatch(messageAction(message));
      setUser(prev => ({ ...prev, loading: false }));
    }
  };

  if (isLoggedIn || redirect) {
    return <Redirect to="/cash" />;
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-card-body">
          <div className="login-header">
            <img src={logo} alt="Shift Tracker Logo" className="login-logo" />
            <h2 className="login-title">{languageData?.data?.login?.title}</h2>
            <p className="login-subtitle">{languageData?.data?.login?.subtitle}</p>
          </div>

          {message && (
            <div className="alert" role="alert">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{message}</span>
            </div>
          )}

          <form className="login-form" onSubmit={handleLogin}>
            <div className="form-floating">
              <input
                type="text"
                className="form-control"
                id="username"
                placeholder={languageData?.data?.login?.username}
                value={user.username}
                onChange={(e) => setUser(prev => ({ ...prev, username: e.target.value }))}
                required
              />
              <label htmlFor="username">{languageData?.data?.login?.username}</label>
            </div>

            <div className="form-floating">
              <input
                type="password"
                className="form-control"
                id="password"
                placeholder={languageData?.data?.login?.password}
                value={user.password}
                onChange={(e) => setUser(prev => ({ ...prev, password: e.target.value }))}
                required
              />
              <label htmlFor="password">{languageData?.data?.login?.password}</label>
            </div>

            <button
              className="login-btn"
              type="submit"
              disabled={user.loading}
            >
              <div className="login-btn-content">
                {user.loading ? (
                  <span className="spinner-border spinner-border-sm me-2"></span>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="me-2">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                {languageData?.data?.login?.signIn}
              </div>
            </button>
          </form>
          
          <div className="login-footer">
            &copy; {new Date().getFullYear()} Shift Tracker - All rights reserved
          </div>
        </div>
      </div>
    </div>
  );
}

export default connect((state: any) => ({
  isLoggedIn: state.authReducer?.isLoggedIn,
  message: state.messageReducer?.message,
}))(Login);
